import React, { useEffect, useRef, useState } from "react";
import { IconButton, Box, Typography } from "@mui/material";
import { DndContext, useDraggable } from "@dnd-kit/core";
import CloseIcon from "@mui/icons-material/Close";
import "./StickyNotesDrawer.scss";

const DraggableDrawer = ({
  children,
  position,
  toggleNoteDrawer,
  drawerRef,
  drawerSize,
}) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: "note-drawer",
  });

  const dragX = transform ? position.x + transform.x : position.x;
  const dragY = transform ? position.y + transform.y : position.y;

  const setRefs = (node) => {
    setNodeRef(node);
    if (drawerRef) drawerRef.current = node;
  };

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleNoteDrawer();
  };

  return (
    <Box
      ref={setRefs}
      className="customDrawer"
      sx={{
        transform: `translate3d(${dragX}px, ${dragY}px, 0)`,
        borderRadius: "8px",
        overflow: "auto",
        width: drawerSize?.width,
        height: drawerSize?.height,
      }}
    >
      <Box className={"sticky-header"} {...listeners} {...attributes}>
        <Typography variant="h6" sx={{ fontStyle: "normal !important" }}>
          Notepad
        </Typography>

        <IconButton
          size="small"
          onClick={handleClose}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {children}
    </Box>
  );
};

const StickyNoteDrawer = ({
  open,
  toggleNoteDrawer,
  containerRef,
  noteStorageKey = "",
}) => {
  // Storage keys
  const NOTE_STORAGE_KEY = noteStorageKey;
  const POSITION_STORAGE_KEY = `${noteStorageKey}-position`;
  const SIZE_STORAGE_KEY = `${noteStorageKey}-size`;

  const DEFAULT_SIZE = { width: 520, height: 440 };

  const loadNoteFromStorage = () => {
    try {
      const savedNote = localStorage.getItem(NOTE_STORAGE_KEY);
      return savedNote || "";
    } catch (error) {
      console.error("Error loading note from localStorage:", error);
      return "";
    }
  };

  const loadPositionFromStorage = () => {
    try {
      const savedPosition = localStorage.getItem(POSITION_STORAGE_KEY);
      return savedPosition ? JSON.parse(savedPosition) : { x: 0, y: 0 };
    } catch (error) {
      console.error("Error loading position from localStorage:", error);
      return { x: 0, y: 0 };
    }
  };

  const loadSizeFromStorage = () => {
    try {
      const savedSize = localStorage.getItem(SIZE_STORAGE_KEY);
      const parsed = savedSize ? JSON.parse(savedSize) : null;
      if (!parsed || typeof parsed !== "object") return DEFAULT_SIZE;

      const width = Number(parsed.width);
      const height = Number(parsed.height);

      return {
        width: Number.isFinite(width) ? width : DEFAULT_SIZE.width,
        height: Number.isFinite(height) ? height : DEFAULT_SIZE.height,
      };
    } catch (error) {
      console.error("Error loading drawer size from localStorage:", error);
      return DEFAULT_SIZE;
    }
  };

  const saveNoteToStorage = (noteToSave) => {
    try {
      localStorage.setItem(NOTE_STORAGE_KEY, noteToSave);
    } catch (error) {
      console.error("Error saving note to localStorage:", error);
    }
  };

  const savePositionToStorage = (positionToSave) => {
    try {
      localStorage.setItem(
        POSITION_STORAGE_KEY,
        JSON.stringify(positionToSave)
      );
    } catch (error) {
      console.error("Error saving position to localStorage:", error);
    }
  };

  const saveSizeToStorage = (sizeToSave) => {
    try {
      localStorage.setItem(SIZE_STORAGE_KEY, JSON.stringify(sizeToSave));
    } catch (error) {
      console.error("Error saving drawer size to localStorage:", error);
    }
  };

  const [note, setNote] = useState(loadNoteFromStorage);
  const [position, setPosition] = useState(loadPositionFromStorage);
  const [drawerSize, setDrawerSize] = useState(loadSizeFromStorage);

  const drawerElRef = useRef(null);

  useEffect(() => {
    saveNoteToStorage(note);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note]);

  useEffect(() => {
    savePositionToStorage(position);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position]);

  useEffect(() => {
    saveSizeToStorage(drawerSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerSize]);

  const constrainPosition = (newPosition, size = drawerSize) => {
    const container = containerRef?.current;

    const width = size?.width ?? DEFAULT_SIZE.width;
    const height = size?.height ?? DEFAULT_SIZE.height;

    if (!container) {
      return {
        x: Math.max(0, Math.min(newPosition.x, window.innerWidth - width)),
        y: Math.max(0, Math.min(newPosition.y, window.innerHeight - height)),
      };
    }

    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    const PADDING = 20;
    const PADDING_LEFT = 80;

    const constrainedX = Math.max(
      PADDING,
      Math.min(newPosition.x, containerWidth - width - PADDING_LEFT)
    );

    const constrainedY = Math.max(
      PADDING,
      Math.min(newPosition.y, containerHeight - height - PADDING)
    );

    return { x: constrainedX, y: constrainedY };
  };

  // Track CSS resize (so width/height + constraints are always correct)
  useEffect(() => {
    if (!open) return;
    const node = drawerElRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;

    let rafId = null;

    const ro = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const next = { width: node.offsetWidth, height: node.offsetHeight };
        setDrawerSize((prev) =>
          prev?.width === next.width && prev?.height === next.height
            ? prev
            : next
        );
      });
    });

    ro.observe(node);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [open]);

  // If user resizes bigger, keep drawer inside bounds
  useEffect(() => {
    if (!open) return;
    setPosition((prev) => constrainPosition(prev, drawerSize));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerSize?.width, drawerSize?.height, open]);

  useEffect(() => {
    if (open && containerRef?.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const savedPosition = loadPositionFromStorage();

      const w = drawerSize?.width ?? DEFAULT_SIZE.width;

      if (savedPosition.x === 0 && savedPosition.y === 0) {
        const initialX = Math.max(0, containerRect.width - w - 20);
        const initialY = 20;
        setPosition(constrainPosition({ x: initialX, y: initialY }));
      } else {
        setPosition(constrainPosition(savedPosition));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, containerRef]);

  return (
    <DndContext
      onDragEnd={(event) => {
        const { delta } = event;
        if (delta) {
          const newPosition = {
            x: position.x + delta.x,
            y: position.y + delta.y,
          };
          setPosition(constrainPosition(newPosition));
        }
      }}
    >
      {open && (
        <DraggableDrawer
          position={position}
          toggleNoteDrawer={toggleNoteDrawer}
          drawerRef={drawerElRef}
          drawerSize={drawerSize}
        >
          <Box className="noteContent">
            <textarea
              className="noteTextarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Paste / edit code here..."
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
              data-gramm="false"
              data-gramm_editor="false"
              data-enable-grammarly="false"
            />
          </Box>
        </DraggableDrawer>
      )}
    </DndContext>
  );
};

export default StickyNoteDrawer;
